import autobind from 'autobind-decorator';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import dynamic from 'next/dynamic';
import React, {FormEvent} from 'react';

import {withMatchMedia} from '@udemy/hooks';
import {Button} from '@udemy/react-core-components';
import {
    CompactFormGroupProps,
    TextInput,
    ValidationState,
    Select,
    TextArea,
} from '@udemy/react-form-components';
import {Loader, Skeleton} from '@udemy/react-reveal-components';
import {
    CMSConnectedPartnersBanner,
    DynamicWebContentPageType,
} from '@udemy/shared-one-udemy-components';
import {UDData, withUDData} from '@udemy/ud-data';

import {ERROR_STATES} from '../../types/marketo/constants';
import {MarketoSelectField} from '../../types/marketo/marketo-select-field.mobx-model';
import {GrowthContainer} from '../growth-container/growth-container.react-component';
import {Terms} from '../terms/terms.react-component';
import {
    DemoFormValueProps,
    DemoFormValuePropsProps,
    DemoFormValuePropsSkeleton,
} from './demo-form-value-props.react-component';
import {RequestDemoStore, MarketoFormFields} from './request-demo-store.mobx-store';
import styles from './request-demo.module.less';

const CompactFormGroup = dynamic<CompactFormGroupProps>(
    () =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (import('@udemy/react-form-components') as Promise<any>).then(
            (module) => module.CompactFormGroup,
        ),
    {
        ssr: false,
    },
);

export interface RequestDemoProps {
    isLoadingValueProps: boolean;
    stackedLayout: boolean;
    userType: string;
    udData: UDData;
    scrollToFirstInvalidField: boolean;
    requestDemoStore: RequestDemoStore;
    heading: string;
    subHeading: string;
    valuePropsHeading: string;
    valueProps: DemoFormValuePropsProps;
}

@withMatchMedia({
    // On desktop, tablet we show a stacked layout
    stackedLayout: 'md-max',
    // For mobile widths, scroll to fisrt invalid field if validation fails on submit
    scrollToFirstInvalidField: 'sm-max',
})
@observer
class RequestDemoInternal extends React.Component<RequestDemoProps> {
    constructor(props: RequestDemoProps) {
        super(props);
        this.store = props.requestDemoStore;
        this.store.setUserType(props.userType);
        this.store.setOriginalReferrerCookie();
    }

    store: RequestDemoStore;

    scrollToInvalidFormField(mktoName: string) {
        const element = document.getElementById(mktoName);
        if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    @autobind
    handleSubmit(ev: FormEvent) {
        ev.preventDefault();

        // If we are on mobile, scroll to the first invalid field
        if (this.props.scrollToFirstInvalidField) {
            this.store.validate();
            for (const field of Object.values(this.store.formFields)) {
                if (field.value === null || field.validationState === ERROR_STATES.ERROR) {
                    this.scrollToInvalidFormField(field.mktoName);
                    break;
                }
            }
        }

        this.store.submit();
    }

    @autobind
    handleFormUpdate(ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        let incomingValue: string | boolean = ev.target.value;
        // type guard if we are in the appropriate ChangeEvent type
        if ('checked' in ev.target && ev.target.hasOwnProperty('checked')) {
            incomingValue = ev.target.checked;
        }
        this.store.formFields[ev.target.name as keyof MarketoFormFields]?.updateValue(
            incomingValue,
        );
    }

    @autobind
    renderFormField(name: keyof MarketoFormFields, renderTextArea = false) {
        const field = this.store.formFields[name];

        if (!field) return null;

        return (
            <CompactFormGroup
                label={<span className={styles['field-label']}>{field.label}</span>}
                validationState={field.validationState as ValidationState}
                note={field.errorMessage}
                className={styles['field']}
            >
                <div id={field.mktoName}>
                    {renderTextArea ? (
                        <TextArea data-testid={name} name={name} {...field.inputProps} />
                    ) : (
                        <TextInput
                            data-testid={name}
                            name={name}
                            onBlur={this.handleFormUpdate}
                            {...field.inputProps}
                        />
                    )}
                </div>
            </CompactFormGroup>
        );
    }

    @autobind
    renderTextAreaField(name: keyof MarketoFormFields) {
        return this.renderFormField(name, true);
    }

    @autobind
    renderSelectFields(keys: (keyof MarketoFormFields)[], compact = false) {
        const renderSelectFormGroup = (name: keyof MarketoFormFields) => {
            const field = this.store.formFields[name] as MarketoSelectField;
            return (
                <CompactFormGroup
                    key={name}
                    label={<span className={styles['field-label']}>{field.label}</span>}
                    validationState={field.validationState as ValidationState}
                    note={field.errorMessage}
                    className={styles['field']}
                >
                    <div id={field.mktoName}>
                        <Select
                            data-testid={name}
                            defaultValue="default-placeholder"
                            onChange={this.handleFormUpdate}
                            name={name}
                        >
                            <option key="placeholder" value="default-placeholder" disabled={true}>
                                {gettext('Select...')}
                            </option>
                            {field.options.map((option) => (
                                <option data-testid={option} value={option} key={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                    </div>
                </CompactFormGroup>
            );
        };
        // Render side-by-side
        if (compact) {
            return (
                <div className={styles['compact']}>
                    {keys.map((name) => {
                        return renderSelectFormGroup(name);
                    })}
                </div>
            );
        }
        // Render stacked
        return keys.map((name) => {
            return renderSelectFormGroup(name);
        });
    }

    renderHeadingSkeleton() {
        return (
            <>
                <Skeleton className={styles['demo-heading-skeleton']} />
                <Skeleton className={styles['demo-subheading-skeleton']} />
                <Skeleton className={styles['demo-subheading-skeleton']} />
            </>
        );
    }

    renderHeading() {
        return (
            <>
                <h1 className={styles['demo-heading']}>{this.props.heading}</h1>
                <div className={styles['demo-subheading-label-container']}>
                    <span
                        className={classNames(
                            'ud-text-responsive',
                            'ud-text-lg',
                            styles['demo-subheading-label'],
                        )}
                        dangerouslySetInnerHTML={{__html: this.props.subHeading}}
                    />
                </div>
                <h3>{this.props.valuePropsHeading}</h3>
            </>
        );
    }

    renderForm() {
        return (
            <div className={styles['form-container']}>
                <form className={styles['form']} onSubmit={this.handleSubmit} noValidate={true}>
                    <div className={styles['compact']}>
                        {this.renderFormField('firstName')}
                        {this.renderFormField('lastName')}
                    </div>
                    {this.renderFormField('email')}
                    {this.renderFormField('phone')}
                    {this.renderSelectFields(['country'])}
                    <div className={styles['spacer']} />
                    {this.renderFormField('companyName')}
                    {this.renderSelectFields(['companySize', 'companyEmployeeSize'], true)}
                    <div className={styles['compact']}>
                        {this.renderFormField('jobTitle')}
                        {this.renderSelectFields(['jobLevel'])}
                    </div>
                    {this.renderTextAreaField('trainingNeeds')}
                    <Button
                        className={styles['submit-button']}
                        data-testid="submit-button"
                        udStyle="primary"
                        size="large"
                        type="submit"
                        disabled={!this.store.isMarketoFormLoaded || this.store.isFormSubmitting}
                        onClick={this.handleSubmit}
                    >
                        {gettext('Submit')}
                        {this.store.isFormSubmitting && (
                            <Loader
                                color="inherit"
                                className={styles['submit-button-loader']}
                                data-testid="submit-loader"
                            />
                        )}
                    </Button>
                    <Terms />
                </form>
            </div>
        );
    }

    render() {
        return (
            <GrowthContainer headerKey="requestDemo">
                <div className={styles['description']}>
                    {this.props.isLoadingValueProps
                        ? this.renderHeadingSkeleton()
                        : this.renderHeading()}

                    {this.props.isLoadingValueProps ? (
                        <DemoFormValuePropsSkeleton />
                    ) : (
                        <DemoFormValueProps {...this.props.valueProps} />
                    )}

                    {this.props.stackedLayout ? this.renderForm() : null}

                    <CMSConnectedPartnersBanner
                        pageType={DynamicWebContentPageType.RequestDemoForm}
                        headingAlignment="left"
                    />
                </div>

                <div className={styles['column-gap']} />

                {this.props.stackedLayout ? null : this.renderForm()}

                <form
                    id={`mktoForm_${this.props.requestDemoStore.formId}`}
                    style={{display: 'none'}}
                />
            </GrowthContainer>
        );
    }
}

export const RequestDemo = withUDData(RequestDemoInternal);
